import React, { useRef, useCallback, useEffect, RefObject, useImperativeHandle } from "react"

// Use a type alias for the DOM event to support both MouseEvent and PointerEvent
type PointerDOMEvent = MouseEvent | PointerEvent

type BallEvent = {
    source: "left" | "right" | "center"
    pos: { left: number; right: number }
}

type DragState = {
    ball: "left" | "right" | "center"
    startX?: number // Client X position at the start of the drag
    initialLeft?: number // Initial left position (%)
    initialRight?: number // Initial right position (%)
}

export interface TimelineScrollbarControls {
    zoom: (position: number, isZoomIn: boolean) => void
    move: (delta: number) => void
}

export const TimelineScrollbar = ({
    onBallMove,
    scrollbarRef,
}: {
    onBallMove: (ballEvent: BallEvent) => void
    scrollbarRef: RefObject<TimelineScrollbarControls | null>
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const leftBallRef = useRef<HTMLDivElement>(null)
    const rightBallRef = useRef<HTMLDivElement>(null)
    const rangeRef = useRef<HTMLDivElement>(null)

    const positionsRef = useRef({ left: 0, right: 100 })
    const draggingRef = useRef<DragState | null>(null)

    const animationFrameRef = useRef<number | null>(null)
    // Corrected type to standard DOM PointerEvent
    const latestPointerEventRef = useRef<PointerDOMEvent | null>(null)
    const needsUpdateRef = useRef<boolean>(false)

    // This is the correct pattern to use a prop in a stable useCallback
    const onBallMoveRef = useRef(onBallMove)
    useEffect(() => {
        onBallMoveRef.current = onBallMove
    })

    const updatePositions = useCallback(() => {
        if (!draggingRef.current || !containerRef.current || !latestPointerEventRef.current) {
            return
        }

        const rect = containerRef.current.getBoundingClientRect()
        const { ball } = draggingRef.current
        const event = latestPointerEventRef.current

        if (ball === "left" || ball === "right") {
            const newX = Math.max(0, Math.min(rect.width, event.clientX - rect.left))
            let newVal = (newX / rect.width) * 100

            if (ball === "left") {
                newVal = Math.min(newVal, positionsRef.current.right)
                positionsRef.current.left = newVal
            } else {
                newVal = Math.max(newVal, positionsRef.current.left)
                positionsRef.current.right = newVal
            }
        } else if (ball === "center") {
            const { startX, initialLeft, initialRight } = draggingRef.current
            if (startX === undefined || initialLeft === undefined || initialRight === undefined)
                return

            const deltaX = event.clientX - startX
            const deltaPercent = (deltaX / rect.width) * 100
            const width = initialRight - initialLeft

            let newLeft = initialLeft + deltaPercent

            if (newLeft < 0) newLeft = 0
            if (newLeft + width > 100) newLeft = 100 - width

            positionsRef.current.left = newLeft
            positionsRef.current.right = newLeft + width
        }

        // Imperatively update DOM styles
        if (leftBallRef.current) leftBallRef.current.style.left = `${positionsRef.current.left}%`
        if (rightBallRef.current) rightBallRef.current.style.left = `${positionsRef.current.right}%`
        if (rangeRef.current) {
            rangeRef.current.style.left = `${positionsRef.current.left}%`
            rangeRef.current.style.width = `${positionsRef.current.right - positionsRef.current.left}%`
        }

        onBallMoveRef.current({ source: ball, pos: positionsRef.current })
        needsUpdateRef.current = false
    }, []) // Stable: depends only on refs

    const animationLoop = useCallback(() => {
        if (needsUpdateRef.current) {
            updatePositions()
        }
        if (draggingRef.current) {
            animationFrameRef.current = requestAnimationFrame(animationLoop)
        }
    }, [updatePositions]) // Stable: updatePositions is stable

    const handlePointerMove = useCallback((event: PointerDOMEvent) => {
        if (!draggingRef.current) return

        // Prevent default browser behavior (e.g., touch scrolling)
        event.preventDefault()

        latestPointerEventRef.current = event
        needsUpdateRef.current = true
    }, []) // Stable: depends only on refs

    const handlePointerUp = useCallback(() => {
        if (!draggingRef.current) return

        document.removeEventListener("pointermove", handlePointerMove)
        document.removeEventListener("pointerup", handlePointerUp)
        document.removeEventListener("pointercancel", handlePointerUp)

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
        }

        if (needsUpdateRef.current) {
            updatePositions()
        }

        draggingRef.current = null
        latestPointerEventRef.current = null
        needsUpdateRef.current = false
    }, [handlePointerMove, updatePositions]) // Stable: dependencies are stable

    // OPTIMIZATION: Memoize startDrag to prevent stale closures
    const startDrag = useCallback(
        (event: React.PointerEvent, dragState: DragState) => {
            event.currentTarget.setPointerCapture(event.pointerId)

            draggingRef.current = dragState
            latestPointerEventRef.current = event.nativeEvent as PointerDOMEvent
            needsUpdateRef.current = true

            document.addEventListener("pointermove", handlePointerMove)
            document.addEventListener("pointerup", handlePointerUp)
            document.addEventListener("pointercancel", handlePointerUp)

            animationFrameRef.current = requestAnimationFrame(animationLoop)
        },
        [animationLoop, handlePointerMove, handlePointerUp] // Dependencies are stable
    )

    const handleBallPointerDown = useCallback(
        (event: React.PointerEvent, ball: "left" | "right") => {
            event.preventDefault()
            startDrag(event, { ball })
        },
        [startDrag] // OPTIMIZATION: Add stable startDrag dependency
    )

    const handleCenterPointerDown = useCallback(
        (event: React.PointerEvent) => {
            event.preventDefault()
            startDrag(event, {
                ball: "center",
                startX: event.clientX,
                initialLeft: positionsRef.current.left,
                initialRight: positionsRef.current.right,
            })
        },
        [startDrag] // OPTIMIZATION: Add stable startDrag dependency
    )

    // Cleanup listeners on unmount
    useEffect(() => {
        // Return cleanup function
        return () => {
            document.removeEventListener("pointermove", handlePointerMove)
            document.removeEventListener("pointerup", handlePointerUp)
            document.removeEventListener("pointercancel", handlePointerUp)
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [handlePointerMove, handlePointerUp]) // Stable: runs only on mount/unmount

    useImperativeHandle(scrollbarRef, () => ({
        zoom: (localPercent: number, isZoomIn: boolean) => {
            const { left, right } = positionsRef.current
            const currentWidth = right - left

            const pivotGlobal = left + (localPercent / 100) * currentWidth

            const SCALE_STEP = 0.2
            const scale = isZoomIn ? 1 - SCALE_STEP : 1 + SCALE_STEP

            let newWidth = currentWidth * scale

            newWidth = Math.max(1, Math.min(100, newWidth))

            let newLeft = pivotGlobal - (localPercent / 100) * newWidth
            let newRight = newLeft + newWidth

            if (newLeft < 0) {
                newLeft = 0
                newRight = newWidth
            } else if (newRight > 100) {
                newRight = 100
                newLeft = 100 - newWidth
            }

            positionsRef.current = { left: newLeft, right: newRight }

            if (leftBallRef.current) leftBallRef.current.style.left = `${newLeft}%`
            if (rightBallRef.current) rightBallRef.current.style.left = `${newRight}%`
            if (rangeRef.current) {
                rangeRef.current.style.left = `${newLeft}%`
                rangeRef.current.style.width = `${newRight - newLeft}%`
            }

            onBallMoveRef.current({ source: "center", pos: positionsRef.current })
        },
        move: (delta: number) => {
            const { left, right } = positionsRef.current
            const width = right - left

            // 1. Calculate Candidate Positions
            let newLeft = left + delta
            let newRight = right + delta

            // 2. Wall Collisions (Clamping)
            // If we hit the left wall, clamp to 0
            if (newLeft < 0) {
                newLeft = 0
                newRight = width
            }
            // If we hit the right wall, clamp to 100
            else if (newRight > 100) {
                newRight = 100
                newLeft = 100 - width
            }

            // 3. Update State & DOM
            positionsRef.current = { left: newLeft, right: newRight }

            if (leftBallRef.current) leftBallRef.current.style.left = `${newLeft}%`
            if (rightBallRef.current) rightBallRef.current.style.left = `${newRight}%`
            if (rangeRef.current) {
                rangeRef.current.style.left = `${newLeft}%`
                rangeRef.current.style.width = `${width}%`
            }

            // 4. Notify Parent
            onBallMoveRef.current({ source: "center", pos: positionsRef.current })
        },
    }))

    return (
        <div className="flex items-center w-full h-4 px-2">
            <div
                ref={containerRef}
                className="relative w-full h-3 bg-white-100 rounded-full"
                style={{ touchAction: "none" }}
            >
                <div
                    ref={rangeRef}
                    onPointerDown={handleCenterPointerDown}
                    className="absolute h-full bg-gray-600 hover:bg-gray-500"
                    style={{
                        left: `${positionsRef.current.left}%`,
                        width: `${positionsRef.current.right - positionsRef.current.left}%`,
                        touchAction: "none",
                    }}
                />
                <div
                    ref={leftBallRef}
                    onPointerDown={(e) => handleBallPointerDown(e, "left")}
                    className="absolute top-1/2 rounded-full h-3 w-3 border border-gray-300 bg-gray-900 cursor-grab active:cursor-grabbing hover:z-50 hover:border-2"
                    style={{
                        left: `${positionsRef.current.left}%`,
                        transform: "translate(-50%, -50%)",
                        touchAction: "none",
                    }}
                />
                <div
                    ref={rightBallRef}
                    onPointerDown={(e) => handleBallPointerDown(e, "right")}
                    className="absolute top-1/2 rounded-full h-3 w-3 border border-gray-300 bg-gray-900 cursor-grab active:cursor-grabbing hover:z-50 hover:border-2"
                    style={{
                        left: `${positionsRef.current.right}%`,
                        transform: "translate(-50%, -50%)",
                        touchAction: "none",
                    }}
                />
            </div>
        </div>
    )
}
